// Components
import CustomIcon from "components/CustomIcon";
import { Box, Typography, Skeleton, Select, MenuItem } from "@mui/material";

// Hooks
import AutocompleteField, {
    useAutocompleteFunctionsLevels,
    useAutocompleteVacancy,
} from "components/Autocomplete/AutocompleteField";

// Others
import icons from "enums/icons";

// Types
import { CRMVacancies } from "services";
import { TBriefData } from "store/slice/Project/projectData/ProjectSlice.types";
import { TVacancy } from "components/Autocomplete/AutocompleteField/hooks/useAutocompleteVacancy";

// Styles
import briefStyles from "../../Brief.styles";
import { useIntl } from "react-intl";
import { FilterOptionsPill } from "components/FilterOptionsPill";
import CantFindData from "components/Pro/CantFindData";
import { isEmpty } from "lodash";

const BriefPosition = ({
    briefData,
    setBriefData,
    isLoading,
    hasError,
    isReadOnly,
    isEditable,
}: {
    briefData: TBriefData;
    setBriefData?: (data: Partial<TBriefData>) => void;
    isLoading?: boolean;
    hasError?: boolean;
    isReadOnly?: boolean;
    isEditable?: boolean;
}) => {
    const classes = briefStyles();
    const intl = useIntl();

    const { vacancyList, placeholderVacancy, getOptionLabelVacancy, renderOptionVacancy } = useAutocompleteVacancy();
    const { functions, hierarchies, placeholderFunctions, placeholderHierarchy } = useAutocompleteFunctionsLevels();

    const selectedVacancy = vacancyList
        .filter((e) => (e as unknown as CRMVacancies).crimson_vacancyid === briefData.position?.crimson_vacancyid)
        .map((vacancy, i) => ({
            ...vacancy,
            id: i,
            name: (vacancy as unknown as CRMVacancies).crimson_name,
        }))[0];

    const selectedFunction = functions.find((f) => (f as any).type === briefData.roleFunction);
    const selectedHierarchy = hierarchies.find((hierarchy) => (hierarchy as any).type === briefData.roleLevel);

    return (
        <>
            {!isReadOnly && (
                <>
                    <Typography className={classes.stepTitle}>Which position is the focus of your brief?</Typography>
                    <Box className={classes.stepTextWrapper}>
                        <CustomIcon icon={icons.crossHair} className={classes.stepIcon} />
                        <Typography className={classes.stepText}>
                            {intl.formatMessage({ id: "brief.crateBrief.step1.explanation" })}
                        </Typography>
                    </Box>
                </>
            )}
            <Box className={classes.positionWrapper} sx={{ mb: isReadOnly ? 2 : 0 }}>
                {!isReadOnly ? (
                    <>
                        {Boolean(vacancyList.length) && hasError && (
                            <Box sx={{ display: "flex", alignItems: "center", pl: 2 }}>
                                <CustomIcon icon={icons.errorTriangle} sx={{ color: "error.main" }} />
                            </Box>
                        )}

                        <Box sx={{ flex: 1, p: 2 }}>
                            {Boolean(vacancyList.length) && !isLoading && (
                                <AutocompleteField
                                    blurOnSelect
                                    inputSize="small"
                                    id="brief-vacancy"
                                    showEndAdorment={false}
                                    options={vacancyList}
                                    placeholder={placeholderVacancy}
                                    renderOption={renderOptionVacancy}
                                    getOptionLabel={getOptionLabelVacancy}
                                    value={selectedVacancy}
                                    popperSx={{ "& .MuiPaper-root": { borderRadius: "32px", padding: "14px 16px" } }}
                                    onOptionSelectCallback={(vacancy: TVacancy | string | null) => {
                                        if (typeof vacancy === "string" || vacancy === null || !setBriefData) {
                                            return;
                                        }

                                        const { id, ...newVacancy } = vacancy;
                                        setBriefData({ position: newVacancy as unknown as CRMVacancies });
                                    }}
                                />
                            )}
                        </Box>

                        <Box sx={{ flex: 1, p: 2 }}>
                            {Boolean(functions.length) && !isLoading && (
                                <Select
                                    id="brief-function"
                                    value={selectedFunction?.type ?? ""}
                                    className={classes.selectField}
                                    displayEmpty
                                    onChange={(e) => {
                                        const value = functions.find((f) => f.type === e.target.value);
                                        if (value && setBriefData)
                                            setBriefData({
                                                roleFunction: (value.type ?? "") as TBriefData["roleFunction"],
                                            });
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            className: classes.selectFilterPaper,
                                        },
                                    }}
                                    renderValue={(value) => {
                                        if (!value)
                                            return (
                                                <Typography color="text.disabled">{placeholderFunctions}</Typography>
                                            );
                                        const selected = functions.find((f) => f.type === value);
                                        return selected ? (
                                            <FilterOptionsPill
                                                shouldShowFullLabels
                                                filterId="function"
                                                type={selected.type}
                                                label={selected.name}
                                                sx={{ width: "fit-content" }}
                                            />
                                        ) : null;
                                    }}
                                >
                                    {functions.map((option) => (
                                        <MenuItem
                                            key={option.id}
                                            value={option.type}
                                            className={classes.selectMenuItem}
                                            sx={{ padding: 0 }}
                                            disableGutters
                                            autoFocus={false}
                                        >
                                            <FilterOptionsPill
                                                filterId="function"
                                                shouldShowFullLabels
                                                type={option.type}
                                                label={option.name}
                                                sx={{ width: "fit-content", cursor: "pointer" }}
                                            />
                                        </MenuItem>
                                    ))}
                                    <Box display="flex" justifyContent="end">
                                        <CantFindData
                                            text="Can't Find What You Need?"
                                            style={{ textDecoration: "underline" }}
                                        />
                                    </Box>
                                </Select>
                            )}
                        </Box>

                        <Box sx={{ flex: 1, p: 2 }}>
                            {Boolean(hierarchies.length) && !isLoading && (
                                <Select
                                    id="brief-hierarchy"
                                    value={selectedHierarchy?.type || ""}
                                    className={classes.selectField}
                                    displayEmpty
                                    onChange={(e) => {
                                        const value = hierarchies.find((h) => h.type === e.target.value);
                                        if (value && setBriefData)
                                            setBriefData({
                                                roleLevel: (value.type ?? "") as TBriefData["roleLevel"],
                                            });
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            className: classes.selectFilterPaper,
                                        },
                                    }}
                                    renderValue={(value) => {
                                        if (!value)
                                            return (
                                                <Typography color="text.disabled">{placeholderHierarchy}</Typography>
                                            );
                                        const selected = hierarchies.find((h) => h.type === value);
                                        return selected ? (
                                            <FilterOptionsPill
                                                shouldShowFullLabels
                                                filterId="hierarchy"
                                                type={selected.type}
                                                label={selected.name}
                                                sx={{ width: "fit-content" }}
                                            />
                                        ) : null;
                                    }}
                                >
                                    {hierarchies.map((option) => (
                                        <MenuItem
                                            key={option.id}
                                            value={option.type}
                                            className={classes.selectMenuItem}
                                            sx={{ padding: 0 }}
                                            disableGutters
                                            autoFocus={false}
                                        >
                                            <FilterOptionsPill
                                                filterId="hierarchy"
                                                shouldShowFullLabels
                                                type={option.type}
                                                label={option.name}
                                                sx={{ width: "fit-content", cursor: "pointer" }}
                                            />
                                        </MenuItem>
                                    ))}
                                    <Box display="flex" justifyContent="end">
                                        <CantFindData
                                            text="Can't Find What You Need?"
                                            style={{ textDecoration: "underline" }}
                                        />
                                    </Box>
                                </Select>
                            )}
                        </Box>
                    </>
                ) : (
                    <Box sx={{ p: 2, width: "100%" }}>
                        <Box
                            sx={{
                                mb: 2,
                                width: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                            }}
                        >
                            <Box display="flex" alignItems="center">
                                <CustomIcon sx={{ fontSize: 18, mr: 1 }} icon={icons.users} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Vacancy Overview
                                </Typography>
                            </Box>{" "}
                        </Box>
                        {isLoading ? (
                            <Skeleton animation="wave" height={40} />
                        ) : (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: { md: "column", lg: "row" },
                                    alignItems: { md: "flex-start", lg: "center" },
                                }}
                            >
                                {Boolean(briefData?.position?.crimson_name) && (
                                    <Typography sx={{ fontSize: 16, mr: 2 }}>
                                        <strong>CRM Vacancy:</strong> {briefData?.position?.crimson_name}
                                    </Typography>
                                )}

                                {!isEmpty(selectedFunction) && (
                                    <Box display="flex" alignItems="center" mr={2}>
                                        <Typography sx={{ fontSize: 16 }}>
                                            <strong>Function:</strong>
                                        </Typography>
                                        <FilterOptionsPill
                                            shouldShowFullLabels
                                            filterId="function"
                                            type={selectedFunction?.type}
                                            label={selectedFunction?.name}
                                            sx={{ width: "fit-content" }}
                                        />
                                    </Box>
                                )}

                                {!isEmpty(selectedHierarchy) && (
                                    <Box display="flex" alignItems="center" mr={2}>
                                        <Typography sx={{ fontSize: 16 }}>
                                            <strong>Hierarchy:</strong>
                                        </Typography>
                                        <FilterOptionsPill
                                            filterId="hierarchy"
                                            type={selectedHierarchy?.type}
                                            label={selectedHierarchy?.name}
                                            sx={{ width: "fit-content" }}
                                        />
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </>
    );
};

export default BriefPosition;
